import type { ExplorerItem, ExplorerSection } from "./explorerData";
import { matchesFilter, matchesSelf } from "./explorerTabSections";

// The Explorer tree as the flat list of rows on screen — what the panel's
// virtualized list renders (NewExplorer's ExplorerTree). Every row is one
// list-item height, so the virtualizer needs no measuring: a section
// header, a tree item at its depth, or a skeleton placeholder while a
// level loads. Pure, so the expand/collapse/filter rules have unit tests.

export type ExplorerRow =
  | {
      kind: "section";
      key: string;
      section: ExplorerSection;
      collapsed: boolean;
    }
  | {
      kind: "item";
      key: string;
      item: ExplorerItem;
      depth: number;
      expandable: boolean;
      expanded: boolean;
      /** Pins under the section header while its children scroll (only
       *  expanded top-level items do — mario's ConnectionTree rule). */
      sticky: boolean;
    }
  | { kind: "skeleton"; key: string; depth: number };

/** Placeholder rows shown while a section or an item's children load. */
export const SKELETON_ROWS = 2;

export interface ExplorerRowsOptions {
  /** Lower-cased, trimmed filter text; "" for none. */
  filter: string;
  expanded: ReadonlySet<string>;
  /** Open or closed, said outright, for items the user clicked or an AI
   *  action expanded or collapsed while the filter was on — over whatever
   *  the filter decided. Transient — dropped with the filter. */
  filterOverrides: ReadonlyMap<string, boolean>;
  collapsedSections: ReadonlySet<string>;
}

export function isItemExpandable(item: ExplorerItem): boolean {
  return (item.children ?? []).length > 0 || !!item.childrenLoading;
}

// Filtering force-expands the path to each hit so it is visible. Inside a
// hit the tree is the user's own: a folder that matched by name shows its
// contents, and the folders in it keep their expanded state.
function openedByFilter(
  item: ExplorerItem,
  filter: string,
  insideMatch: boolean,
): boolean {
  if (insideMatch) return false;
  if (!filter) return false;
  return isItemExpandable(item);
}

// A filter-time override has the last word over what the filter decided.
export function isItemExpanded(
  item: ExplorerItem,
  options: ExplorerRowsOptions,
  insideMatch = false,
): boolean {
  const { filter, expanded, filterOverrides } = options;
  const open =
    expanded.has(item.id) || openedByFilter(item, filter, insideMatch);
  const override = filter ? filterOverrides.get(item.id) : undefined;
  return override ?? open;
}

function skeletonRows(prefix: string, depth: number): ExplorerRow[] {
  return Array.from({ length: SKELETON_ROWS }, (_, i) => ({
    kind: "skeleton" as const,
    key: `${prefix}:skeleton:${i}`,
    depth,
  }));
}

/** Where an item sits in the walk: its depth, and whether a folder above
 *  it matched by name — from there down nothing is filtered out. */
interface WalkPosition {
  depth: number;
  insideMatch: boolean;
}

// A hit on an item's own name includes everything inside it.
function passesFilter(
  item: ExplorerItem,
  filter: string,
  insideMatch: boolean,
): boolean {
  if (insideMatch) return true;
  if (!filter) return true;
  return matchesFilter(item, filter);
}

function itemRow(
  item: ExplorerItem,
  depth: number,
  expanded: boolean,
): ExplorerRow {
  const expandable = isItemExpandable(item);
  return {
    kind: "item",
    key: item.id,
    item,
    depth,
    expandable,
    expanded,
    sticky: expandable && expanded && depth === 0,
  };
}

// An open item's rows below it: skeletons while its children load, else
// the children themselves.
function pushChildren(
  rows: ExplorerRow[],
  item: ExplorerItem,
  options: ExplorerRowsOptions,
  below: WalkPosition,
) {
  if (item.childrenLoading) {
    rows.push(...skeletonRows(item.id, below.depth));
    return;
  }
  for (const child of item.children ?? []) {
    pushItem(rows, child, options, below);
  }
}

function pushItem(
  rows: ExplorerRow[],
  item: ExplorerItem,
  options: ExplorerRowsOptions,
  at: WalkPosition,
) {
  const { filter } = options;
  if (!passesFilter(item, filter, at.insideMatch)) return;
  const expanded = isItemExpanded(item, options, at.insideMatch);
  rows.push(itemRow(item, at.depth, expanded));
  if (!expanded) return;
  const selfMatch = !!filter && matchesSelf(item, filter);
  pushChildren(rows, item, options, {
    depth: at.depth + 1,
    insideMatch: at.insideMatch || selfMatch,
  });
}

// A section collapses only without a filter (a filter shows every hit);
// a loading section shows skeletons in place of its items.
function pushSection(
  rows: ExplorerRow[],
  section: ExplorerSection,
  options: ExplorerRowsOptions,
) {
  const collapsed =
    options.collapsedSections.has(section.id) && !options.filter;
  rows.push({ kind: "section", key: section.id, section, collapsed });
  if (collapsed) return;
  if (section.loading) {
    rows.push(...skeletonRows(section.id, 0));
    return;
  }
  for (const item of section.items) {
    pushItem(rows, item, options, { depth: 0, insideMatch: false });
  }
}

export function flattenExplorerRows(
  sections: ExplorerSection[],
  options: ExplorerRowsOptions,
): ExplorerRow[] {
  const rows: ExplorerRow[] = [];
  for (const section of sections) pushSection(rows, section, options);
  return rows;
}

/** The rows pinned at the top of a viewport whose first visible row is
 *  `startIndex`: the section header above it, and within that section the
 *  expanded top-level item whose subtree holds it (null once the subtree
 *  has scrolled past — a later top-level row ends it). */
export function stickyRowsAt(
  rows: readonly ExplorerRow[],
  startIndex: number,
): { section: number | null; item: number | null } {
  let section: number | null = null;
  for (let i = Math.min(startIndex, rows.length - 1); i >= 0; i--) {
    if (rows[i].kind === "section") {
      section = i;
      break;
    }
  }
  if (section === null) return { section: null, item: null };
  let item: number | null = null;
  for (let i = section + 1; i <= startIndex && i < rows.length; i++) {
    const row = rows[i];
    if (row.kind === "section" || row.depth !== 0) continue;
    item = row.kind === "item" && row.sticky ? i : null;
  }
  return { section, item };
}
